import type { EnumKeys } from "@/types/utils.ts";

import { branded } from "@/lib/helpers/branded-helper";
import { anemicReference } from "@/types/backoffice/dto/common/references";

export enum EPhotoType {
  DELIVERY_POINT = "DELIVERY_POINT",
  CONTRACT = "CONTRACT",
  BEFORE_INSTALL_CLOSED = "BEFORE_INSTALL_CLOSED",
  BEFORE_INSTALL_OPENED = "BEFORE_INSTALL_OPENED",
  AFTER_INSTALL_CLOSED = "AFTER_INSTALL_CLOSED",
  AFTER_INSTALL_OPENED = "AFTER_INSTALL_OPENED",
  OTHER = "OTHER",
  HEATERS = "HEATERS",
  INSTALLATION_SHEET_VOLTAPAP = "INSTALLATION_SHEET_VOLTAPAP",
  ELECTRICAL_BOARD = "ELECTRICAL_BOARD",
  INSTALLATION_SHEET_VOLTINSTALL = "INSTALLATION_SHEET_VOLTINSTALL",
  CASKET = "CASKET",
  INSTALL_BEFORE_INSTALL = "INSTALL_BEFORE_INSTALL",
  INSTALL_AFTER_WIRING = "INSTALL_AFTER_WIRING",
  INSTALL_AFTER_REASSEMBLY = "INSTALL_AFTER_REASSEMBLY",
  UNINSTALL_BEFORE_UNINSTALL = "UNINSTALL_BEFORE_UNINSTALL",
  UNINSTALL_AFTER_REASSEMBLY = "UNINSTALL_AFTER_REASSEMBLY",
  BEFORE_WIRING = "BEFORE_WIRING",
  INITIAL_INVENTORY = "INITIAL_INVENTORY",
  APPOINTMENT_COMMITMENT_FORM = "APPOINTMENT_COMMITMENT_FORM",
  ELECTRICITY_BILL = "ELECTRICITY_BILL",
  CEE_QUOTE = "CEE_QUOTE",
  NEW_IMPORT_INSTALLATION_SHEET = "NEW_IMPORT_INSTALLATION_SHEET",
  PROGRAMMING_MODE_STICKER = "PROGRAMMING_MODE_STICKER",
  WIRE_MODE = "WIRE_MODE",
  NAME_PLATE = "NAME_PLATE",
  ELECTRICAL_BOARD_AND_HOUSING = "ELECTRICAL_BOARD_AND_HOUSING",
  RADIATORS_AND_TOWEL_DRYERS = "RADIATORS_AND_TOWEL_DRYERS",
  HOT_WATER_TANK = "HOT_WATER_TANK",
}

export interface IPhotoType {
  readonly id: number;
  readonly description: EPhotoType;
}

export const photoTypeSchema = anemicReference(branded.photoTypeId);

export const PhotoTypes: { [K in EnumKeys<typeof EPhotoType>]: IPhotoType } = {
  [EPhotoType.DELIVERY_POINT]: {
    id: 1,
    description: EPhotoType.DELIVERY_POINT,
  },
  [EPhotoType.CONTRACT]: {
    id: 2,
    description: EPhotoType.CONTRACT,
  },
  [EPhotoType.BEFORE_INSTALL_CLOSED]: {
    id: 3,
    description: EPhotoType.BEFORE_INSTALL_CLOSED,
  },
  [EPhotoType.BEFORE_INSTALL_OPENED]: {
    id: 4,
    description: EPhotoType.BEFORE_INSTALL_OPENED,
  },
  [EPhotoType.AFTER_INSTALL_CLOSED]: {
    id: 5,
    description: EPhotoType.AFTER_INSTALL_CLOSED,
  },
  [EPhotoType.AFTER_INSTALL_OPENED]: {
    id: 6,
    description: EPhotoType.AFTER_INSTALL_OPENED,
  },
  [EPhotoType.OTHER]: {
    id: 7,
    description: EPhotoType.OTHER,
  },
  [EPhotoType.HEATERS]: {
    id: 8,
    description: EPhotoType.HEATERS,
  },
  [EPhotoType.INSTALLATION_SHEET_VOLTAPAP]: {
    id: 9,
    description: EPhotoType.INSTALLATION_SHEET_VOLTAPAP,
  },
  [EPhotoType.ELECTRICAL_BOARD]: {
    id: 10,
    description: EPhotoType.ELECTRICAL_BOARD,
  },
  [EPhotoType.INSTALLATION_SHEET_VOLTINSTALL]: {
    id: 11,
    description: EPhotoType.INSTALLATION_SHEET_VOLTINSTALL,
  },
  [EPhotoType.CASKET]: {
    id: 12,
    description: EPhotoType.CASKET,
  },
  [EPhotoType.INSTALL_BEFORE_INSTALL]: {
    id: 13,
    description: EPhotoType.INSTALL_BEFORE_INSTALL,
  },
  [EPhotoType.INSTALL_AFTER_WIRING]: {
    id: 14,
    description: EPhotoType.INSTALL_AFTER_WIRING,
  },
  [EPhotoType.INSTALL_AFTER_REASSEMBLY]: {
    id: 15,
    description: EPhotoType.INSTALL_AFTER_REASSEMBLY,
  },
  [EPhotoType.UNINSTALL_BEFORE_UNINSTALL]: {
    id: 16,
    description: EPhotoType.UNINSTALL_BEFORE_UNINSTALL,
  },
  [EPhotoType.UNINSTALL_AFTER_REASSEMBLY]: {
    id: 17,
    description: EPhotoType.UNINSTALL_AFTER_REASSEMBLY,
  },
  [EPhotoType.BEFORE_WIRING]: {
    id: 18,
    description: EPhotoType.BEFORE_WIRING,
  },
  [EPhotoType.INITIAL_INVENTORY]: {
    id: 19,
    description: EPhotoType.INITIAL_INVENTORY,
  },
  [EPhotoType.NEW_IMPORT_INSTALLATION_SHEET]: {
    id: 20,
    description: EPhotoType.NEW_IMPORT_INSTALLATION_SHEET,
  },
  [EPhotoType.APPOINTMENT_COMMITMENT_FORM]: {
    id: 21,
    description: EPhotoType.APPOINTMENT_COMMITMENT_FORM,
  },
  [EPhotoType.ELECTRICITY_BILL]: {
    id: 22,
    description: EPhotoType.ELECTRICITY_BILL,
  },
  [EPhotoType.CEE_QUOTE]: {
    id: 23,
    description: EPhotoType.CEE_QUOTE,
  },
  [EPhotoType.PROGRAMMING_MODE_STICKER]: {
    id: 24,
    description: EPhotoType.PROGRAMMING_MODE_STICKER,
  },
  [EPhotoType.WIRE_MODE]: {
    id: 25,
    description: EPhotoType.WIRE_MODE,
  },
  [EPhotoType.NAME_PLATE]: {
    id: 26,
    description: EPhotoType.NAME_PLATE,
  },
  [EPhotoType.ELECTRICAL_BOARD_AND_HOUSING]: {
    id: 27,
    description: EPhotoType.ELECTRICAL_BOARD_AND_HOUSING,
  },
  [EPhotoType.RADIATORS_AND_TOWEL_DRYERS]: {
    id: 28,
    description: EPhotoType.RADIATORS_AND_TOWEL_DRYERS,
  },
  [EPhotoType.HOT_WATER_TANK]: {
    id: 29,
    description: EPhotoType.HOT_WATER_TANK,
  },
};

export const PhotoTypesUploadable: Array<IPhotoType> = [
  PhotoTypes.DELIVERY_POINT,
  PhotoTypes.CONTRACT,
  PhotoTypes.INSTALLATION_SHEET_VOLTAPAP,
  PhotoTypes.INSTALLATION_SHEET_VOLTINSTALL,
  PhotoTypes.NEW_IMPORT_INSTALLATION_SHEET,
  PhotoTypes.ELECTRICAL_BOARD,
  PhotoTypes.ELECTRICITY_BILL,
  PhotoTypes.CEE_QUOTE,
];

export function getPhotoTypeById(id: number): IPhotoType | undefined {
  return Object.values(PhotoTypes).find((photoType) => photoType.id === id);
}
